import React, { useEffect, useState } from "react"
import Reveal from "./Reveal.jsx"

// Social-proof section (2026-07-17). Renders admin-published merchant
// testimonials from /api/v1/public/testimonials — real names, real shops,
// metrics frozen at the moment the merchant consented. Renders NOTHING
// while the list is empty (no empty state, the section simply doesn't
// exist yet), so it can ship ahead of the first published quote.
export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([])

  useEffect(() => {
    let cancelled = false
    fetch("/api/v1/public/testimonials")
      .then((r) => (r.ok ? r.json() : { testimonials: [] }))
      .then((data) => {
        if (!cancelled) setTestimonials(data.testimonials || [])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  if (testimonials.length === 0) return null

  return (
    <section className="border-t border-[color:var(--color-rule)] bg-[color:var(--color-surface-card)]">
      <div className="max-w-6xl mx-auto px-6 py-24">
        <Reveal>
          {/* Deliberately no § number — this section renders only once the
              first testimonial is published, and a conditional section
              inside the hardcoded 01–06 sequence would leave a numbering
              gap whenever it's absent. */}
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-4">
            From the merchants
          </p>
          <h2 className="font-sans text-[40px] tracking-[-0.035em] font-medium leading-[1.1] max-w-2xl">
            Real shops, real numbers.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, idx) => (
            <Reveal key={`${t.shop_name}-${idx}`} delay={idx * 120}>
              <figure className="h-full flex flex-col gap-6 border border-[color:var(--color-rule)] rounded-[var(--radius-card-lg)] bg-[color:var(--color-surface-page)] p-8">
                <blockquote className="font-sans text-[17px] leading-[1.55] tracking-[-0.01em] text-[color:var(--color-ink-primary)]">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>

                <figcaption className="mt-auto flex items-center gap-4">
                  {t.logo_url ? (
                    <img
                      src={t.logo_url}
                      alt={`${t.shop_name} logo`}
                      className="w-10 h-10 rounded-[var(--radius-button)] object-contain bg-white border border-[color:var(--color-rule)]"
                      loading="lazy"
                    />
                  ) : (
                    <span className="w-10 h-10 rounded-[var(--radius-button)] bg-[color:var(--color-brand)] text-white font-sans font-medium text-[15px] inline-flex items-center justify-center">
                      {(t.shop_name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="flex flex-col">
                    <span className="font-sans text-[14px] font-medium text-[color:var(--color-ink-primary)]">
                      {t.author_name}
                      {t.author_role ? (
                        <span className="text-[color:var(--color-ink-tertiary)] font-normal"> · {t.author_role}</span>
                      ) : null}
                    </span>
                    <a
                      href={t.shop_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[11px] tracking-[0.03em] text-[color:var(--color-ink-tertiary)] hover:text-[color:var(--color-ai)]"
                    >
                      {t.shop_name}
                    </a>
                  </span>
                </figcaption>

                {t.metrics && t.metrics.winners_count > 0 ? (
                  <p className="font-mono text-[11px] tracking-[0.04em] text-[color:var(--color-winner)] border-t border-[color:var(--color-rule)] pt-4">
                    {t.metrics.winners_count} winning {t.metrics.winners_count === 1 ? "test" : "tests"}
                    {Number(t.metrics.cumulative_lift_pct) > 0
                      ? ` · +${Number(t.metrics.cumulative_lift_pct).toFixed(1)}% cumulative lift`
                      : ""}
                  </p>
                ) : null}
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}
